/**
 * Phone display helpers for the admin dashboard.
 *
 * The invitations list is an operator screen that may stay open on a shared
 * monitor, so a pending invitation shows a masked number: enough to recognise the
 * number just typed (country code + last four), not a readable phone directory.
 *
 * Display only — never use these for identity, matching, or anything sent back
 * to the server. The backend owns normalization (phone_normalize.phone_key).
 */

/** Mask an E.164 number for display: "+15551234567" → "+1 ••• ••• 4567".
 *
 *  A value that is not E.164-shaped (legacy rows predating invite-time
 *  validation) still falls back to last-four masking, so an unexpected shape
 *  never leaks the number. */
export function maskPhone(raw: string | null | undefined): string {
  const s = (raw ?? "").trim();
  if (!s) return "—";

  const digits = s.replace(/\D/g, "");
  if (digits.length <= 4) return "•".repeat(Math.max(digits.length, 2));

  const last4 = digits.slice(-4);
  if (s.startsWith("+")) {
    // Country code = whatever precedes the 10-digit national number, capped so a
    // malformed value can't render an absurd prefix.
    const ccLen = Math.max(0, Math.min(digits.length - 10, 3));
    const cc = ccLen > 0 ? `+${digits.slice(0, ccLen)} ` : "+";
    return `${cc}••• ••• ${last4}`;
  }
  return `••• ••• ${last4}`;
}

/** Human phrasing for an invite-SMS reason code. The backend decides; this only
 *  says it in words an operator can act on. */
export function inviteSmsReasonText(reason: string | null | undefined): string {
  switch (reason) {
    case "channel_disabled":
      return "Invite text messages are turned off — share the registration link manually.";
    case "registration_disabled":
      return "Self-registration is turned off, so no registration link was texted.";
    case "invalid_destination":
      return "That number could not be texted.";
    case "country_blocked":
      return "That country is not enabled for text messages.";
    case "suppressed":
      return "That number has opted out of text messages (STOP).";
    case "suppression_unavailable":
      return "Opt-out status could not be checked, so no text was sent.";
    case "not_configured":
      return "The text-message sender is not configured on this server.";
    case "destination_limit":
    case "global_limit":
      return "Too many invite texts recently — try again later.";
    case "send_failed":
      return "The text message could not be delivered.";
    default:
      return "The invitation was not texted.";
  }
}
